import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import {
   SettingsField,
   SettingsLbl,
   SettingsInput,
   SettingsHintSm,
} from './ispSettingsUI';
import type { IspPaymentDraft } from '@/api/isp';

/**
 * Money received from a customer.
 *
 * ⚠️ IT IS NOT TIED TO ONE INVOICE, and that is deliberate. A customer hands
 * over ₱2,000 against whatever they owe; deciding which bill it clears is
 * arithmetic, not a question for the person holding the cash. The server applies
 * it oldest-first — the oldest invoice is the one about to get them cut off.
 *
 * The outstanding total is shown so the operator can see when they are being
 * handed more than is owed, before they take it.
 */
interface Props {
   customerId: string;
   customerName: string;
   /** What this invoice still owes — shown as a sanity check, not a limit. */
   outstanding: number;
   onCancel: () => void;
   onSave: (draft: IspPaymentDraft) => void;
}

const METHODS: { id: IspPaymentDraft['method']; label: string }[] = [
   { id: 'cash', label: 'Cash' },
   { id: 'gcash', label: 'GCash' },
   { id: 'bank', label: 'Bank transfer' },
   { id: 'other', label: 'Other' },
];

const IspPaymentForm: React.FC<Props> = ({
   customerId,
   customerName,
   outstanding,
   onCancel,
   onSave,
}) => {
   const [amount, setAmount] = useState(String(outstanding || ''));
   const [method, setMethod] = useState<IspPaymentDraft['method']>('cash');
   const [reference, setReference] = useState('');
   const [notes, setNotes] = useState('');
   const [error, setError] = useState('');

   const value = Number(amount);
   const over = Number.isFinite(value) && value > outstanding && outstanding > 0;

   const submit = () => {
      if (!Number.isFinite(value) || value <= 0) {
         setError('How much did they pay?');
         return;
      }
      setError('');
      onSave({
         customer: customerId,
         amount: Math.round(value * 100) / 100,
         method,
         reference: reference.trim(),
         notes: notes.trim(),
      });
   };

   return (
      <Wrapper>
         <Lead>
            From <strong>{customerName}</strong>. This clears their oldest unpaid
            bill first, then the next.
         </Lead>

         <Grid>
            <SettingsField>
               <SettingsLbl>Amount (₱)</SettingsLbl>
               <SettingsInput
                  type="number"
                  min={0}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
               />
               <SettingsHintSm>
                  {outstanding > 0
                     ? `This invoice still owes ₱${outstanding.toLocaleString()}.`
                     : 'Nothing outstanding on this invoice.'}
               </SettingsHintSm>
            </SettingsField>

            <SettingsField>
               <SettingsLbl>How they paid</SettingsLbl>
               <Sel
                  value={method}
                  onChange={(e) =>
                     setMethod(e.target.value as IspPaymentDraft['method'])
                  }
               >
                  {METHODS.map((m) => (
                     <option key={m.id} value={m.id}>
                        {m.label}
                     </option>
                  ))}
               </Sel>
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Reference (optional)</SettingsLbl>
               <SettingsInput
                  value={reference}
                  placeholder="GCash ref, deposit slip…"
                  onChange={(e) => setReference(e.target.value)}
               />
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Notes (optional)</SettingsLbl>
               <SettingsInput
                  value={notes}
                  placeholder="Anything worth remembering"
                  onChange={(e) => setNotes(e.target.value)}
               />
            </SettingsField>
         </Grid>

         {/* Warned BEFORE they take it, not after it is recorded. Change is the
             customer's money, and an operator who knows now can hand it back
             across the counter instead of owing it. */}
         {over && (
            <OverNote>
               That is <strong>₱{(value - outstanding).toLocaleString()}</strong>{' '}
               more than this invoice owes. Anything left after their older bills
               are cleared comes back as change — it is not kept.
            </OverNote>
         )}

         {error && <Err>{error}</Err>}

         <Actions>
            <Ghost type="button" onClick={onCancel}>
               Cancel
            </Ghost>
            <Primary type="button" onClick={submit}>
               Record payment
            </Primary>
         </Actions>
      </Wrapper>
   );
};

export default IspPaymentForm;

const Wrapper = styled.div`
   width: 560px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 12px;
`;
const Lead = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.6;
   color: ${colors.GRAY5};
`;
const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 12px 16px;
   @media (max-width: 620px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;
const Sel = styled.select`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   width: 100%;
`;
const OverNote = styled.p`
   margin: 0;
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid ${colors.ORANGE}55;
   background: ${colors.ORANGE}10;
   font-size: 12px;
   line-height: 1.6;
   color: ${colors.MAIN_DARK};
`;
const Err = styled.p`
   margin: 0;
   font-size: 12px;
   color: ${colors.DANGER};
`;
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;
const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
`;
const Primary = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
`;
